import { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import ServiceabilityMapService, {
  ServiceabilitySweepPoint,
} from "../../services/ServiceabilityMapService";

type ServiceKey = "chefit" | "bookByDish" | "party";

const SERVICE_OPTIONS: { key: ServiceKey; label: string }[] = [
  { key: "chefit", label: "CHEFIT" },
  { key: "bookByDish", label: "BOOK_BY_DISH" },
  { key: "party", label: "PARTY" },
];

const GREEN = "#16a34a";
const RED = "#dc2626";
// Lighter shades used for fallback (nearest-area) points.
const GREEN_LIGHT = "#86efac";
const RED_LIGHT = "#fca5a5";
const DEFAULT_CENTER = { lat: 28.44918, lng: 77.030228 };
// Zoom the map snaps to whenever a filter changes.
const FILTER_ZOOM = 11;

type RenderPoint = {
  lat: number;
  lng: number;
  serviceable: boolean;
  inPoly: boolean;
  data: ServiceabilitySweepPoint;
};

// Minimal shape of the imperatively-built canvas overlay instance.
interface CanvasOverlay {
  setMap(map: google.maps.Map | null): void;
  setData(points: RenderPoint[]): void;
  hitTest(latLng: google.maps.LatLng): RenderPoint | null;
}

const isServiceable = (p: ServiceabilitySweepPoint, service: ServiceKey): boolean => {
  if (service === "chefit") return !!p.isChefitServiceable;
  if (service === "bookByDish") return !!p.isBookByDishServiceable;
  return !!p.isPartyServiceable;
};

const getLatLng = (p: ServiceabilitySweepPoint): { lat: number; lng: number } | null => {
  const lat = Number(p.lat);
  const lng = Number(p.long);
  if (!isNaN(lat) && !isNaN(lng) && Math.abs(lat) > 0.1 && Math.abs(lng) > 0.1) {
    return { lat, lng };
  }
  if (p.coordinates?.coordinates && p.coordinates.coordinates.length >= 2) {
    const [cLng, cLat] = p.coordinates.coordinates;
    if (!isNaN(cLat) && !isNaN(cLng)) return { lat: cLat, lng: cLng };
  }
  return null;
};

function ServiceabilityMap() {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY as string,
  });

  const service = useMemo(() => new ServiceabilityMapService(), []);
  const navigate = useNavigate();

  const [points, setPoints] = useState<ServiceabilitySweepPoint[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceKey>("chefit");
  const [showInPolygon, setShowInPolygon] = useState(true);
  const [showFallback, setShowFallback] = useState(true);
  const [loading, setLoading] = useState(false);

  const mapRef = useRef<google.maps.Map | null>(null);
  const overlayRef = useRef<CanvasOverlay | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const boundsFittedRef = useRef(false);
  const didMountFiltersRef = useRef(false);
  const selectedServiceRef = useRef<ServiceKey>(selectedService);

  // State to restore when returning from an area page (saved before navigating).
  type RestoreState = { lat: number; lng: number; zoom: number; pointId: number };
  const restoreRef = useRef<RestoreState | null>(
    (() => {
      try {
        const raw = sessionStorage.getItem("serviceabilityFocus");
        return raw ? (JSON.parse(raw) as RestoreState) : null;
      } catch {
        return null;
      }
    })()
  );
  // Id of the point whose popup should be re-opened after returning.
  const pendingPopupIdRef = useRef<number | null>(restoreRef.current?.pointId ?? null);
  // Initial map view — restored focus at the same zoom, or the default city view.
  // Computed once so the props stay stable.
  const initialView = useRef(
    restoreRef.current
      ? {
          center: { lat: restoreRef.current.lat, lng: restoreRef.current.lng },
          zoom: restoreRef.current.zoom,
        }
      : { center: DEFAULT_CENTER, zoom: 11 }
  ).current;

  // Keep a ref of the selected service so the (once-bound) click handler
  // always reads the current value instead of a stale closure.
  useEffect(() => {
    selectedServiceRef.current = selectedService;
  }, [selectedService]);

  // Fetch all serviceability-sweep points exactly once (service is stable).
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    service
      .getServiceabilitySweep()
      .then((data) => {
        if (mounted) setPoints(data);
      })
      .catch((e) => console.error("[ServiceabilityMap] fetch error:", e))
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [service]);

  // Points that pass the in-polygon / fallback visibility filters.
  const visiblePoints = useMemo(() => {
    return points.filter((p) => {
      const inPoly = !!p.isInsidePolygon;
      if (inPoly && !showInPolygon) return false;
      if (!inPoly && !showFallback) return false;
      return true;
    });
  }, [points, showInPolygon, showFallback]);

  const renderPoints = useMemo<RenderPoint[]>(() => {
    const out: RenderPoint[] = [];
    for (const p of visiblePoints) {
      const coords = getLatLng(p);
      if (!coords) continue;
      out.push({
        lat: coords.lat,
        lng: coords.lng,
        serviceable: isServiceable(p, selectedService),
        inPoly: !!p.isInsidePolygon,
        data: p,
      });
    }
    return out;
  }, [visiblePoints, selectedService]);

  const counts = useMemo(() => {
    let yes = 0;
    let no = 0;
    for (const p of renderPoints) {
      if (p.serviceable) yes++;
      else no++;
    }
    return { yes, no, total: yes + no };
  }, [renderPoints]);

  // Open the info popup for a given point (used on click and when restoring
  // focus after returning from an area page).
  const openInfo = (rp: RenderPoint) => {
    const map = mapRef.current;
    const iw = infoWindowRef.current;
    if (!map || !iw) return;
    const d = rp.data;
    const label =
      SERVICE_OPTIONS.find((s) => s.key === selectedServiceRef.current)?.label ?? "";

    // Prefer the enclosing area id, fall back to the resolved area id, else null.
    const areaId = d.enclosingAreaId ?? d.resolvedAreaId ?? null;
    const areaName = d.enclosingAreaName ?? d.resolvedAreaName ?? null;

    // Inline onclick guarantees the focus (point + current zoom) is saved
    // before we navigate to the area page.
    if (areaId != null) {
      (window as any).__svcAreaGo = () => {
        sessionStorage.setItem(
          "serviceabilityFocus",
          JSON.stringify({
            lat: rp.lat,
            lng: rp.lng,
            zoom: map.getZoom() ?? initialView.zoom,
            pointId: d.id,
          })
        );
        navigate(`/Areas/${areaId}/show`);
        return false;
      };
    }

    const areaRow =
      areaId != null
        ? `Area: <a href="#" onclick="return window.__svcAreaGo && window.__svcAreaGo();" style="color:#2563EB;font-weight:600;text-decoration:none;cursor:pointer">#${areaId}${areaName ? " · " + areaName : ""}</a>`
        : `Area: <span style="color:#64748b">null</span>`;

    iw.setContent(
      `<div style="font-size:12px;line-height:1.5">
        <b>${label}: ${rp.serviceable ? "Serviceable" : "Not serviceable"}</b><br/>
        ${d.cityName ?? ""}<br/>
        ${areaRow}<br/>
        lat ${rp.lat.toFixed(5)}, lng ${rp.lng.toFixed(5)}<br/>
        ${rp.inPoly ? "In-polygon" : "Fallback (nearest area)"}<br/>
        CHEFIT: ${d.isChefitServiceable ? "✓" : "✗"} &nbsp;
        BBD: ${d.isBookByDishServiceable ? "✓" : "✗"} &nbsp;
        PARTY: ${d.isPartyServiceable ? "✓" : "✗"}
      </div>`
    );
    iw.setPosition({ lat: rp.lat, lng: rp.lng });
    iw.open(map);
  };

  // Build the canvas overlay once the map is ready. A single <canvas> that
  // redraws on zoom/pan keeps thousands of points smooth (no per-point markers).
  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (!infoWindowRef.current) {
      infoWindowRef.current = new google.maps.InfoWindow();
    }

    class PointsOverlay extends google.maps.OverlayView implements CanvasOverlay {
      private data: RenderPoint[] = [];
      private canvas: HTMLCanvasElement | null = null;

      setData(pts: RenderPoint[]) {
        this.data = pts;
        this.draw();
      }

      hitTest(latLng: google.maps.LatLng): RenderPoint | null {
        const projection = this.getProjection();
        if (!projection) return null;
        const target = projection.fromLatLngToDivPixel(latLng);
        if (!target) return null;
        let best: RenderPoint | null = null;
        let bestDist = 8; // px threshold
        for (const p of this.data) {
          const px = projection.fromLatLngToDivPixel(
            new google.maps.LatLng(p.lat, p.lng)
          );
          if (!px) continue;
          const d = Math.hypot(px.x - target.x, px.y - target.y);
          if (d < bestDist) {
            bestDist = d;
            best = p;
          }
        }
        return best;
      }

      onAdd() {
        const canvas = document.createElement("canvas");
        canvas.style.position = "absolute";
        canvas.style.pointerEvents = "none"; // let the map handle drag/zoom
        this.canvas = canvas;
        this.getPanes()?.overlayLayer.appendChild(canvas);
      }

      onRemove() {
        this.canvas?.parentNode?.removeChild(this.canvas);
        this.canvas = null;
      }

      draw() {
        const projection = this.getProjection();
        const map = this.getMap() as google.maps.Map | null;
        if (!projection || !this.canvas || !map) return;
        const bounds = map.getBounds();
        if (!bounds) return;

        const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
        const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
        if (!sw || !ne) return;

        const left = Math.min(sw.x, ne.x);
        const top = Math.min(sw.y, ne.y);
        const width = Math.abs(ne.x - sw.x);
        const height = Math.abs(sw.y - ne.y);
        const dpr = window.devicePixelRatio || 1;

        this.canvas.style.left = `${left}px`;
        this.canvas.style.top = `${top}px`;
        this.canvas.style.width = `${width}px`;
        this.canvas.style.height = `${height}px`;
        this.canvas.width = Math.max(1, Math.round(width * dpr));
        this.canvas.height = Math.max(1, Math.round(height * dpr));

        const ctx = this.canvas.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);

        // Grow dots slightly as you zoom in so dense grids read as smooth
        // shaded regions when zoomed out, and clean dots when zoomed in.
        const zoom = map.getZoom() ?? 11;
        const zoomBoost = Math.max(-1, Math.min(3.5, (zoom - 11) * 0.55));

        for (const p of this.data) {
          const px = projection.fromLatLngToDivPixel(
            new google.maps.LatLng(p.lat, p.lng)
          );
          if (!px) continue;
          const x = px.x - left;
          const y = px.y - top;
          if (x < -10 || y < -10 || x > width + 10 || y > height + 10) continue;

          const r = (p.inPoly ? 4 : 3.5) + zoomBoost;
          ctx.beginPath();
          ctx.arc(x, y, Math.max(1.5, r), 0, Math.PI * 2);
          // Fallback (nearby / nearest-area) points use a lighter colour;
          // true-containment points use the full-strength colour.
          if (p.inPoly) {
            ctx.fillStyle = p.serviceable ? GREEN : RED;
            ctx.globalAlpha = 0.92;
          } else {
            ctx.fillStyle = p.serviceable ? GREEN_LIGHT : RED_LIGHT;
            ctx.globalAlpha = 0.7;
          }
          ctx.fill();

          // True-containment points get a crisp white halo for separation;
          // fallback points get a faint matching outline.
          if (p.inPoly) {
            ctx.globalAlpha = 0.9;
            ctx.lineWidth = 0.75;
            ctx.strokeStyle = "rgba(255,255,255,0.85)";
            ctx.stroke();
          } else {
            ctx.globalAlpha = 0.55;
            ctx.lineWidth = 0.5;
            ctx.strokeStyle = p.serviceable ? GREEN_LIGHT : RED_LIGHT;
            ctx.stroke();
          }
        }
        ctx.globalAlpha = 1;
      }
    }

    const overlay = new PointsOverlay();
    overlay.setMap(map);
    overlayRef.current = overlay;
    overlay.setData(renderPoints);

    map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (!overlayRef.current || !e.latLng) return;
      const hit = overlayRef.current.hitTest(e.latLng);
      if (!hit) return;
      openInfo(hit);
    });

    // Returning from an area page: restore the exact center AND zoom the user
    // had, and prevent fitToPointsOnce from overriding it. The clicked point's
    // popup is re-opened once data loads.
    if (restoreRef.current) {
      const r = restoreRef.current;
      map.setCenter({ lat: r.lat, lng: r.lng });
      map.setZoom(r.zoom);
      boundsFittedRef.current = true;
      sessionStorage.removeItem("serviceabilityFocus");
      restoreRef.current = null;
    } else {
      // Fit once if data already arrived before the map finished loading.
      fitToPointsOnce();
    }
    reopenPendingPopup();
  };

  const fitToPointsOnce = () => {
    if (!isLoaded || !mapRef.current || boundsFittedRef.current) return;
    if (renderPoints.length === 0) return;
    const b = new google.maps.LatLngBounds();
    renderPoints.forEach((p) => b.extend({ lat: p.lat, lng: p.lng }));
    if (!b.isEmpty()) {
      mapRef.current.fitBounds(b);
      boundsFittedRef.current = true;
    }
  };

  // After returning from an area page, re-open the popup of the clicked point
  // once the data (and map) are available.
  const reopenPendingPopup = () => {
    if (pendingPopupIdRef.current == null) return;
    if (!mapRef.current || !infoWindowRef.current || renderPoints.length === 0) return;
    const rp = renderPoints.find((p) => p.data.id === pendingPopupIdRef.current);
    if (rp) {
      openInfo(rp);
      pendingPopupIdRef.current = null;
    }
  };

  // Push new data into the overlay (redraw only — no map re-init), fit once,
  // and re-open the pending popup if we're returning from an area page.
  useEffect(() => {
    overlayRef.current?.setData(renderPoints);
    fitToPointsOnce();
    reopenPendingPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderPoints, isLoaded]);

  // Whenever a filter changes, snap the map back to a fixed zoom level
  // (centered on the currently visible points). Skips the initial mount.
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    if (!didMountFiltersRef.current) {
      didMountFiltersRef.current = true;
      return;
    }
    let center = DEFAULT_CENTER;
    if (renderPoints.length > 0) {
      const sum = renderPoints.reduce(
        (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
        { lat: 0, lng: 0 }
      );
      center = {
        lat: sum.lat / renderPoints.length,
        lng: sum.lng / renderPoints.length,
      };
    }
    mapRef.current.setZoom(FILTER_ZOOM);
    mapRef.current.panTo(center);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, showInPolygon, showFallback, isLoaded]);

  // Tear down the overlay on unmount.
  useEffect(() => {
    return () => {
      overlayRef.current?.setMap(null);
      overlayRef.current = null;
    };
  }, []);

  if (!isLoaded) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="70vh">
        <Typography fontWeight={600}>Loading serviceability map...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      {/* Filter / legend bar — kept above the map so the map stays clean */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          flexWrap: "wrap",
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "#ffffff",
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mr: 1 }}>
          Serviceability Map
        </Typography>

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="serviceability-service">Service</InputLabel>
          <Select
            labelId="serviceability-service"
            value={selectedService}
            label="Service"
            onChange={(e: SelectChangeEvent<string>) =>
              setSelectedService(e.target.value as ServiceKey)
            }
          >
            {SERVICE_OPTIONS.map((s) => (
              <MenuItem key={s.key} value={s.key}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showInPolygon}
              onChange={(e) => setShowInPolygon(e.target.checked)}
            />
          }
          label={<Typography variant="body2">In-polygon (true containment)</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showFallback}
              onChange={(e) => setShowFallback(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Fallback (nearest area)</Typography>}
        />

        {/* Inline legend */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: { md: "auto" }, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 13, height: 13, borderRadius: "50%", bgcolor: GREEN }} />
            <Typography variant="body2">
              Serviceable{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {counts.yes.toLocaleString()}
              </Box>
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 13, height: 13, borderRadius: "50%", bgcolor: RED }} />
            <Typography variant="body2">
              Not serviceable{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {counts.no.toLocaleString()}
              </Box>
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Showing {counts.total.toLocaleString()} of {points.length.toLocaleString()}
          </Typography>
          {loading && <Chip label="Loading data…" size="small" sx={{ bgcolor: "#e2e8f0" }} />}
        </Box>
      </Box>

      {/* Map + loading overlay */}
      <Box sx={{ position: "relative", height: "calc(100vh - 130px)", minHeight: 400 }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={initialView.center}
          zoom={initialView.zoom}
          onLoad={handleMapLoad}
          options={{ gestureHandling: "greedy" }}
        />
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.55)",
              gap: 1.5,
              zIndex: 10,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              Loading map data…
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ServiceabilityMap;
